import { useEffect, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "@/shared/lib/cx";
import { Icon, type IconName } from "./Icon";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "danger-ghost" | "soft";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  /** Leading icon: a glyph name or any node. */
  icon?: IconName | ReactNode;
  /** Trailing icon (e.g. a chevron). */
  iconEnd?: IconName;
  block?: boolean;
}

const glyph = (icon: IconName | ReactNode, size: ButtonProps["size"]) =>
  typeof icon === "string" ? <Icon name={icon as IconName} size={size === "sm" ? 15 : 17} /> : icon;

export function Button({ variant = "secondary", size = "md", loading, icon, iconEnd, block, children, className, type = "button", ...rest }: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={cx("btn", `btn-${variant}`, size !== "md" && `btn-${size}`, block && "btn-block", className)}
      disabled={loading || rest.disabled}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="spinner" aria-hidden /> : icon && glyph(icon, size)}
      {children}
      {iconEnd && !loading && <Icon name={iconEnd} size={size === "sm" ? 15 : 17} />}
    </button>
  );
}

/** Square button holding one glyph; `label` names it for screen readers and the tooltip. */
export function IconButton({ label, icon, variant = "ghost", size = "sm", className, ...rest }: Omit<ButtonProps, "aria-label" | "icon" | "children"> & {
  label: string; icon: IconName;
}) {
  return <Button variant={variant} size={size} aria-label={label} title={label} className={cx("btn-icon", className)} icon={icon} {...rest} />;
}

const DISARM_MS = 5000;

/** Two-step destructive action without blocking browser dialogs (disarms itself after a few seconds). */
export function ConfirmButton({ onConfirm, children, loading, confirmLabel = "Xác nhận", icon = "trash", size = "sm", iconOnly, label, variant = "danger-ghost" }: {
  onConfirm: () => void; children?: ReactNode; loading?: boolean; confirmLabel?: string; icon?: IconName;
  size?: ButtonProps["size"];
  /** Show only the icon until armed (row actions); `label` then names it. */
  iconOnly?: boolean; label?: string; variant?: ButtonVariant;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), DISARM_MS);
    return () => clearTimeout(id);
  }, [armed]);

  if (armed) {
    return (
      <span className="btn-group">
        <Button variant="danger" size={size} icon="check" loading={loading} autoFocus onClick={() => { setArmed(false); onConfirm(); }}>{confirmLabel}</Button>
        <Button variant="ghost" size={size} onClick={() => setArmed(false)}>Huỷ</Button>
      </span>
    );
  }
  return iconOnly
    ? <IconButton label={label ?? String(children ?? "Xoá")} icon={icon} variant={variant} size={size} loading={loading} onClick={() => setArmed(true)} />
    : <Button variant={variant} size={size} icon={icon} loading={loading} onClick={() => setArmed(true)}>{children}</Button>;
}
